// @flow

import { Component } from '../jsti/core';
import { decodeURIComponentSafe, objectHasValue, percentageVisible } from '../classes/Helpers';
import EventNames from '../constants/EventNames';

export default class EventNotifier extends Component {
  #triggers: Array<string> = [];
  #data: Object;
  #observer: ?IntersectionObserver = null;
  #hasBeenTriggered: boolean = false;
  #delay: number;
  #treshold: number = 0.5;
  #GA4 = false;

  /**
   * Retrieve the observer
   */
  get observer(): ?IntersectionObserver {
    return this.#observer;
  }

  /**
   * Get the delay in milliseconds
   */
  get delay(): number {
    return this.#delay;
  }

  /**
   * Instantiate the component
   */
  bind(el: HTMLElement): void {
    super.bind(el);

    try {
      this.#triggers = JSON.parse(decodeURIComponentSafe(this.el.dataset.on));
    } catch {
      this.#triggers.push(this.el.dataset.on);
    }
    this.#GA4 = !!this.el.dataset.ga4;
    this.#data = this.el.dataset.eventData ? JSON.parse(decodeURIComponentSafe(this.el.dataset.eventData)) : {};
    this.#delay = +this.el.dataset.delay || 0;
    Object.keys(this.#triggers).forEach((trigger: string) => {
      if (
        !trigger ||
        !objectHasValue(EventNames, trigger)
      ) {
        throw new Error('The trigger event is not defined or invalid');
      }

      if (trigger === EventNames.VIEW) {
        setTimeout(() => {
          this.#observer = 'IntersectionObserver' in window
            ? new IntersectionObserver((entries: Array<IntersectionObserverEntry>) => {
              entries.forEach((entry: IntersectionObserverEntry) => this.handleIntersection(entry));
            }, {
              threshold: this.#treshold
            })
            : null;

          if (this.#observer) {
            this.#observer.observe(this.el);
          } else {
            setTimeout(() => {
              this.handleViewTrigger();
            }, 0);
          }
        }, 0);
      } else {
        this.addEventListener(trigger, (event: Event) => this.requestDispatch(event, trigger, false));
      }
    });
  }

  /**
   * Checks whether a request should be send to dispatch an event
   */
  handleViewTrigger(): void {
    if (this.#hasBeenTriggered) return;
    this.#hasBeenTriggered = true;

    if (this.#observer) {
      this.#observer.unobserve(this.el);
    }

    this.requestDispatch(null, EventNames.VIEW, this.el.dataset.shouldQueue === 'true');
  }

  /**
   * Executes when the IntersectionObserver gets triggered
   */
  handleIntersection(entry: IntersectionObserverEntry): void {
    if (!entry.isIntersecting) return;
    setTimeout(() => {
      this.handleViewTrigger();
    }, 0);
  }

  /**
   * Send a notification containing the event that needs to be dispatched
   */
  requestDispatch(event: ?Event = null, on: ?string = null, shouldQueue: boolean = false): void {
    // TODO remove event notifier component check when removing UA events
    if (event && this.el.parentElement.dataset.component !== 'event-notifier') {
      event.stopPropagation();
    }
    this.dispatch({
      event: this.#triggers[on],
      data: this.#data,
      element: event?.target,
      delay: this.#delay,
      shouldQueue: shouldQueue,
      GA4: this.#GA4
    });
  }
}
